import {
  CertificateCheckIcon,
  ClipboardCheckIcon,
  FileRatingIcon,
  GarageIcon,
  HandHoldingCarIcon,
  ReplacementCarsIcon,
  SnowflakeIcon,
} from "@App/Components/common/Icons";
import { HomeBenefitsSection } from "@App/Components/molecules/whitelabel/home/sections/HomeBenefitsSection";
import { HowItWorksSection } from "@App/Components/molecules/whitelabel/home/sections/HowItWorksSection";
import type { State } from "@App/state";
import { getConfig } from "@App/utils/getConfig";
import type { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const STEP_KEYS = ["compare", "appointment", "arrival", "billing"];

const BENEFIT_ICONS = {
  oneStop: <GarageIcon />,
  partner: <HandHoldingCarIcon />,
  originalParts: <CertificateCheckIcon />,
  vehicleData: <FileRatingIcon />,
  replacementCar: <ReplacementCarsIcon />,
  manufacturerSpec: <ClipboardCheckIcon />,
  certifiedAirConditioning: <SnowflakeIcon />,
} as const;

export type HomeBenefitKey = keyof typeof BENEFIT_ICONS;

const DEFAULT_BENEFIT_KEYS: HomeBenefitKey[] = [
  "oneStop",
  "partner",
  "originalParts",
  "vehicleData",
  "replacementCar",
  "manufacturerSpec",
];

export const HomeSections: FunctionComponent = () => {
  const organization = useSelector((state: State) => state.orders.organization);
  const { t } = useTranslation();

  if (!organization) {
    return null;
  }

  const { homeSections } = getConfig(organization);
  const organizationName = organization.name;
  const image = homeSections?.image;
  const showBenefits = homeSections?.showBenefits !== false;
  const benefitKeys = homeSections?.benefitItems ?? DEFAULT_BENEFIT_KEYS;

  return (
    <>
      <HowItWorksSection
        title={t("homeSections.howItWorks.title", { organization: organizationName })}
        note={t("homeSections.howItWorks.note")}
        image={image}
        steps={STEP_KEYS.map((key) => ({
          title: t(`homeSections.howItWorks.steps.${key}.title`, {
            organization: organizationName,
          }),
          description: t(`homeSections.howItWorks.steps.${key}.description`, {
            organization: organizationName,
          }),
        }))}
      />
      {showBenefits && (
        <HomeBenefitsSection
          title={t("homeSections.benefits.title", { organization: organizationName })}
          benefits={benefitKeys.map((key) => ({
            icon: BENEFIT_ICONS[key],
            title: t(`homeSections.benefits.items.${key}.title`, {
              organization: organizationName,
            }),
            description: t(`homeSections.benefits.items.${key}.description`, {
              organization: organizationName,
            }),
          }))}
        />
      )}
    </>
  );
};
